import React, { useEffect, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { connect } from 'react-redux';
import classNames from 'classnames';

import UpdateButton from '../../Template/UpdateButton';
import EventSelector from '../../../components/EventSelector/Modal';
import EventSelectorButton from '../../../components/EventSelector/Button';
import ModelPrompts from './ModelPrompts';
import ImageUpload from './ImageUpload';
import PriorityOrder from './PriorityOrder';

import style from './style.module.scss';

import { updateEventsGeneric } from '../../../helpers/api';
import { fetchEvent } from '../../Template/api';
import { displaySuccessModal, displayErrorModal } from '../../../helpers/swal';

const MODEL_KEYS = [
  'google-gemini-3-pro-image-preview',
  'replicate-bytedance-seedream-5-lite',
  'replicate-google-gemini-3-pro-image-preview',
];

const buildPriorityOrder = (priority) => {
  const order = [...(priority || [])];
  MODEL_KEYS.forEach((k) => {
    if (!order.includes(k)) order.push(k);
  });
  return order.map((key) => ({ key, enabled: (priority || []).includes(key) }));
};

const DEFAULT_CONCEPT = {
  title: '',
  models: {
    'google-gemini-3-pro-image-preview': '',
    'replicate-bytedance-seedream-5-lite': '',
    'replicate-google-gemini-3-pro-image-preview': '',
  },
  priority: [],
  imageInputs: [],
};

const AiTemplateConcept = ({ slugDate }) => {
  const [loading, setLoading] = useState(true);
  const [conceptData, setConceptData] = useState(DEFAULT_CONCEPT);
  const [priorityOrder, setPriorityOrder] = useState(() => buildPriorityOrder(
    DEFAULT_CONCEPT.priority,
  ));

  useEffect(() => {
    const loadEvent = async () => {
      if (!slugDate) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const eventData = await fetchEvent(slugDate);

        if (eventData?.selfiecam?.concept) {
          const { selfiecam: { concept } } = eventData;
          setConceptData(concept);
          setPriorityOrder(buildPriorityOrder(concept.priority));
        } else {
          setConceptData(DEFAULT_CONCEPT);
          setPriorityOrder(buildPriorityOrder(DEFAULT_CONCEPT.priority));
        }
      } catch (error) {
        console.error('Error fetching event:', error);
        displayErrorModal('Failed to load event data');
      } finally {
        setLoading(false);
      }
    };

    loadEvent();
  }, [slugDate]);

  const handleTitleChange = (value) => {
    setConceptData((prev) => ({
      ...prev,
      title: value,
    }));
  };

  const updateEventHandler = async (eventIds) => {
    if (!eventIds?.length || !conceptData) return;
    const priority = priorityOrder.filter((p) => p.enabled).map((p) => p.key);
    const payload = { ...conceptData, priority, imageInputs: conceptData.imageInputs ?? [] };
    await updateEventsGeneric(eventIds, { 'selfiecam.concept': payload });
    displaySuccessModal('AI Concept updated successfully!');
  };

  return (
    <div className={classNames('d-flex', 'flex-column', 'flex-grow-1')}>
      <div className="pageHeader">
        <h4>AI Concept</h4>
        <EventSelectorButton showFutureEvents />
        <UpdateButton label="Update Event" updateHandler={updateEventHandler} />
      </div>

      <div className={classNames('d-flex', 'flex-column', 'flex-grow-1', style.conceptPage)}>
        {loading && (
          <div className="flex-grow-1 d-flex justify-content-center align-items-center">
            <Spinner animation="border" variant="primary" />
          </div>
        )}

        {!loading && (
          <div className={style.conceptPageContent}>
            <div className={style.conceptPageColumns}>
              <div className={style.conceptPagePromptsCol}>
                <div className="mb-4">
                  <label className="form-label w-100" htmlFor="concept-title">
                    <span className="d-block">Title</span>
                    <input
                      id="concept-title"
                      type="text"
                      className="form-control mt-1"
                      value={conceptData?.title || ''}
                      onChange={(e) => handleTitleChange(e.target.value)}
                      placeholder="What is this concept about?"
                    />
                  </label>
                </div>

                <ModelPrompts
                  value={conceptData?.models ?? {}}
                  onChange={(models) => setConceptData((prev) => ({ ...prev, models }))}
                />
              </div>

              <div className={style.conceptPageRightCol}>
                <PriorityOrder
                  value={priorityOrder}
                  onChange={setPriorityOrder}
                />
                <ImageUpload
                  slugDate={slugDate}
                  value={conceptData?.imageInputs || []}
                  onChange={(next) => setConceptData((prev) => ({
                    ...prev,
                    imageInputs: Array.isArray(next) ? next : [],
                  }))}
                />
              </div>
            </div>
          </div>
        )}

        <EventSelector singleSelection showTemplate showFutureEvents />
      </div>
    </div>
  );
};

export default connect(
  ({ events: { selectedEvents } }) => ({
    slugDate: selectedEvents[0]?.slugDate,
  }),
)(AiTemplateConcept);
